import { useState } from 'react';
import { useAuth } from '../auth/context.js';
import { apiFetch, ApiError } from '../api/client.js';
import { Icon } from '../components/Icon.js';
import { initials } from '../lib/initials.js';
import { labels } from '../labels.js';
import styles from './ProfilePage.module.css';

interface Field {
  name: string;
  values: string[];
}

function fieldError(error: unknown): Field | null {
  if (!(error instanceof ApiError) || error.code !== 'VALIDATION_ERROR') return null;
  const details = error.details as { fieldErrors?: Record<string, string[]> } | null;
  if (!details?.fieldErrors) return null;
  const entries = Object.entries(details.fieldErrors);
  const first = entries[0];
  if (!first) return null;
  const [field, messages] = first;
  return { name: field, values: messages ?? [] };
}

function formatLastLogin(value: string | null): string {
  if (!value) return labels.profile.neverConnected;
  return new Date(value).toLocaleString('fr-FR', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function ProfilePage() {
  const { auth, updateUser } = useAuth();
  const user = auth?.user;

  const [firstName, setFirstName] = useState(user?.firstName ?? '');
  const [lastName, setLastName] = useState(user?.lastName ?? '');
  const [infoBanner, setInfoBanner] = useState<string | null>(null);
  const [infoError, setInfoError] = useState<string | null>(null);
  const [savingInfo, setSavingInfo] = useState(false);

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [pwdBanner, setPwdBanner] = useState<string | null>(null);
  const [pwdError, setPwdError] = useState<string | null>(null);
  const [savingPwd, setSavingPwd] = useState(false);

  if (!user) return null;

  const submitInfo = async (e: React.FormEvent) => {
    e.preventDefault();
    setSavingInfo(true);
    setInfoBanner(null);
    setInfoError(null);
    try {
      const dto = await apiFetch<typeof user>('/admin/users/me', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ firstName, lastName }),
      });
      updateUser(dto);
      setInfoBanner(labels.profile.saved);
    } catch (error) {
      const field = fieldError(error);
      setInfoError(field?.values[0] ?? labels.errors.generic);
    } finally {
      setSavingInfo(false);
    }
  };

  const submitPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setPwdBanner(null);
    setPwdError(null);
    if (newPassword !== confirmPassword) {
      setPwdError(labels.profile.mismatch);
      return;
    }
    setSavingPwd(true);
    try {
      await apiFetch('/admin/users/me/password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ currentPassword, newPassword }),
      });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setPwdBanner(labels.profile.passwordChanged);
    } catch (error) {
      if (error instanceof ApiError && error.code === 'INVALID_CURRENT_PASSWORD') {
        setPwdError(labels.profile.invalidCurrentPassword);
      } else {
        setPwdError(labels.errors.generic);
      }
    } finally {
      setSavingPwd(false);
    }
  };

  return (
    <div className="page">
      <header className="page-header">
        <div>
          <h1 className="page-title">{labels.profile.title}</h1>
          <p className="page-subtitle">{labels.profile.subtitle}</p>
        </div>
      </header>

      <div className={styles.layout}>
        <aside className={`card ${styles.identity}`}>
          <span className="avatar avatar--lg">
            {user.photoUrl ? <img src={user.photoUrl} alt={labels.profile.photoAlt} /> : initials(user.firstName, user.lastName)}
          </span>
          <div>
            <p className={styles.name}>{user.firstName} {user.lastName}</p>
            <p className={styles.email}>{user.email}</p>
          </div>
          <span className="badge badge--outline">{labels.team.roleLabels[user.role] ?? user.role}</span>

          <dl className={styles.facts}>
            <div>
              <dt><Icon name="store" size={16} />{labels.profile.restaurant}</dt>
              {/* Nul pour un administrateur de plateforme, par construction. */}
              <dd>{auth?.restaurant?.name ?? labels.profile.noRestaurant}</dd>
            </div>
            <div>
              <dt><Icon name="clock" size={16} />{labels.profile.lastLogin}</dt>
              <dd>{formatLastLogin(user.lastLoginAt)}</dd>
            </div>
          </dl>
        </aside>

        <div className="stack">
          <form onSubmit={submitInfo} className="card card--flush">
            <header className="card-header">
              <div>
                <h2 className="card-title">{labels.profile.sectionIdentity}</h2>
                <p className="field-hint">{labels.profile.identityHelp}</p>
              </div>
            </header>
            <div className="card-body form-grid">
              {infoBanner && <p role="status" className="notice notice--success" style={{ margin: 0 }}><Icon name="check-circle" size={18} />{infoBanner}</p>}
              {infoError && <p role="alert" className="notice notice--danger" style={{ margin: 0 }}><Icon name="alert" size={18} />{infoError}</p>}
              <div className="form-grid form-grid--2">
                <div className="field">
                  <label htmlFor="profile-first" className="field-label">{labels.profile.firstName}</label>
                  <input id="profile-first" className="input" value={firstName} onChange={(e) => setFirstName(e.target.value)} required />
                </div>
                <div className="field">
                  <label htmlFor="profile-last" className="field-label">{labels.profile.lastName}</label>
                  <input id="profile-last" className="input" value={lastName} onChange={(e) => setLastName(e.target.value)} required />
                </div>
              </div>
            </div>
            <footer className="card-footer">
              <button type="submit" className="btn-primary" disabled={savingInfo}>
                {labels.profile.save}
              </button>
            </footer>
          </form>

          <form onSubmit={submitPassword} className="card card--flush">
            <header className="card-header">
              <div>
                <h2 className="card-title">{labels.profile.sectionPassword}</h2>
                <p className="field-hint">{labels.profile.passwordHint}</p>
              </div>
              <Icon name="lock" className="text-muted" />
            </header>
            <div className="card-body form-grid">
              {pwdBanner && <p role="status" className="notice notice--success" style={{ margin: 0 }}><Icon name="check-circle" size={18} />{pwdBanner}</p>}
              {pwdError && <p role="alert" className="notice notice--danger" style={{ margin: 0 }}><Icon name="alert" size={18} />{pwdError}</p>}
              <div className="field">
                <label htmlFor="profile-current" className="field-label">{labels.profile.currentPassword}</label>
                <input id="profile-current" className="input" type="password" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} required autoComplete="current-password" />
              </div>
              <div className="form-grid form-grid--2">
                <div className="field">
                  <label htmlFor="profile-new" className="field-label">{labels.profile.newPassword}</label>
                  <input id="profile-new" className="input" type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} required autoComplete="new-password" />
                </div>
                <div className="field">
                  <label htmlFor="profile-confirm" className="field-label">{labels.profile.confirmPassword}</label>
                  <input id="profile-confirm" className="input" type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required autoComplete="new-password" />
                </div>
              </div>
            </div>
            <footer className="card-footer">
              <button type="submit" className="btn-primary" disabled={savingPwd}>
                {labels.profile.changePassword}
              </button>
            </footer>
          </form>
        </div>
      </div>
    </div>
  );
}
